// commentsStore.ts
import { create } from "zustand"
import {
  Comment,
  CommentStatus,
  CommentAuditEntry,
  CreateCommentData,
  CreateReplyData,
} from "types/comments.types"
import { CommentStorageProvider } from "services/comments/CommentStorageProvider"
import { exportAuditLogToCsv } from "utils/exportToCsv"

interface CommentsStore {
  // State
  comments: Comment[]
  activeCommentId: string | null
  isInitialized: boolean
  isConnected: boolean
  isPanelOpen: boolean
  isFormOpen: boolean
  formSelectedText: string
  formPosition: { x: number; y: number } | null
  /** Indicates new comments arrived via websocket while panel was closed */
  hasNewComments: boolean
  /** IDs of comments that are new (for highlighting in panel) */
  newCommentIds: Set<string>

  // Provider
  provider: CommentStorageProvider | null

  // Provider Management
  initializeProvider: (
    provider: CommentStorageProvider,
    projectId: string,
    variantName?: string,
    owner?: string
  ) => Promise<void>
  disposeProvider: () => void

  // Comment Actions
  addComment: (commentData: CreateCommentData) => string
  updateComment: (id: string, updates: Partial<Comment>) => void
  deleteComment: (
    id: string,
    deletedBy: string,
    deletedByName: string,
    reason: Comment["deletionReason"]
  ) => void

  // Reply Actions
  addReply: (commentId: string, reply: CreateReplyData) => string
  deleteReply: (commentId: string, replyId: string) => void

  // Status Actions
  setStatus: (
    id: string,
    status: CommentStatus,
    userId: string,
    userName: string
  ) => void
  resolveComment: (id: string, resolvedBy: string, resolvedByName: string) => void

  // UI State Actions
  setActiveComment: (id: string | null) => void
  openPanel: () => void
  closePanel: () => void
  togglePanel: () => void
  openForm: (selectedText: string, position?: { x: number; y: number }) => void
  closeForm: () => void

  // Notification Actions
  markNewComments: (commentIds: string[]) => void
  clearNewComments: () => void

  // Queries
  getActiveComments: () => Comment[]
  getCommentById: (id: string) => Comment | null
  getCommentsByVariant: (variantName: string) => Comment[]
  getAuditLog: (commentId?: string) => CommentAuditEntry[]

  // Export
  exportAuditLog: (projectName?: string) => boolean

  // Sync
  refreshComments: () => void
}

export const useCommentsStore = create<CommentsStore>((set, get) => ({
  // Initial State
  comments: [],
  activeCommentId: null,
  isInitialized: false,
  isConnected: false,
  isPanelOpen: false,
  isFormOpen: false,
  formSelectedText: "",
  formPosition: null,
  hasNewComments: false,
  newCommentIds: new Set<string>(),
  provider: null,

  // Provider Management
  initializeProvider: async (provider, projectId, variantName = "", owner = "") => {
    // Dispose existing provider if any
    const existingProvider = get().provider
    if (existingProvider) {
      existingProvider.dispose()
    }

    // Initialize new provider
    await provider.initialize(projectId, variantName, owner)

    // Track known comment IDs to detect new remote comments
    let knownCommentIds = new Set(provider.getAll().map((c) => c.id))

    // Subscribe to changes
    provider.onCommentsChanged((comments) => {
      const { isPanelOpen, markNewComments } = get()

      // Detect new comments (not in our known set)
      const newCommentIds = comments
        .filter((c) => !c.isDeleted && !knownCommentIds.has(c.id))
        .map((c) => c.id)

      // Update known IDs
      knownCommentIds = new Set(comments.map((c) => c.id))

      // Mark new comments for notification if panel is closed
      if (newCommentIds.length > 0 && !isPanelOpen) {
        markNewComments(newCommentIds)
      }

      set({ comments })
    })

    provider.onConnectionStatusChanged?.((isConnected) => {
      set({ isConnected })
    })

    // Store provider and initial comments
    set({
      provider,
      comments: provider.getAll(),
      isInitialized: true,
      isConnected: provider.isReady(),
    })
  },

  disposeProvider: () => {
    const { provider } = get()
    if (provider) {
      provider.dispose()
    }
    set({
      provider: null,
      comments: [],
      isInitialized: false,
      isConnected: false,
      activeCommentId: null,
    })
  },

  // Comment Actions
  addComment: (commentData) => {
    const { provider } = get()
    if (!provider) return ""
    return provider.add(commentData)
  },

  updateComment: (id, updates) => {
    const { provider } = get()
    if (!provider) return
    provider.update(id, updates)
  },

  deleteComment: (id, deletedBy, deletedByName, reason) => {
    const { provider, activeCommentId } = get()
    if (!provider) return
    provider.delete(id, deletedBy, deletedByName, reason)

    // Clear active comment if it was deleted
    if (activeCommentId === id) {
      set({ activeCommentId: null })
    }
  },

  // Reply Actions
  addReply: (commentId, reply) => {
    const { provider } = get()
    if (!provider) return ""
    return provider.addReply(commentId, reply)
  },

  deleteReply: (commentId, replyId) => {
    const { provider } = get()
    if (!provider) return
    provider.deleteReply(commentId, replyId)
  },

  // Status Actions
  setStatus: (id, status, userId, userName) => {
    const { provider } = get()
    if (!provider) return
    provider.setStatus(id, status, userId, userName)
  },

  resolveComment: (id, resolvedBy, resolvedByName) => {
    const { provider } = get()
    if (!provider) return
    provider.resolve(id, resolvedBy, resolvedByName)
  },

  // UI State Actions
  setActiveComment: (id) => {
    set({ activeCommentId: id })
  },

  openPanel: () => {
    // Clear new comments notification when panel is opened
    set({ isPanelOpen: true, hasNewComments: false, newCommentIds: new Set() })
  },

  closePanel: () => {
    set({ isPanelOpen: false })
  },

  togglePanel: () => {
    set((state) => {
      const newState: Partial<CommentsStore> = { isPanelOpen: !state.isPanelOpen }
      // Clear notification when opening
      if (!state.isPanelOpen) {
        newState.hasNewComments = false
        newState.newCommentIds = new Set()
      }
      return newState
    })
  },

  openForm: (selectedText, position) => {
    set({
      isFormOpen: true,
      formSelectedText: selectedText,
      formPosition: position || null,
    })
  },

  closeForm: () => {
    set({
      isFormOpen: false,
      formSelectedText: "",
      formPosition: null,
    })
  },

  // Notification Actions
  markNewComments: (commentIds) => {
    if (commentIds.length === 0) return
    set((state) => {
      // Only show notification if panel is closed
      if (state.isPanelOpen) return {}
      const newIds = new Set(state.newCommentIds)
      commentIds.forEach((id) => newIds.add(id))
      return { hasNewComments: true, newCommentIds: newIds }
    })
  },

  clearNewComments: () => {
    set({ hasNewComments: false, newCommentIds: new Set() })
  },

  // Queries
  getActiveComments: () => {
    const { comments } = get()
    return comments.filter((c) => !c.isDeleted)
  },

  getCommentById: (id) => {
    const { comments } = get()
    return comments.find((c) => c.id === id) || null
  },

  getCommentsByVariant: (variantName) => {
    const { comments } = get()
    return comments.filter((c) => c.variantName === variantName && !c.isDeleted)
  },

  getAuditLog: (commentId) => {
    const { provider } = get()
    if (!provider) return []
    return provider.getAuditLog(commentId)
  },

  // Export
  exportAuditLog: (projectName) => {
    const { provider } = get()
    if (!provider) return false
    const entries = provider.getAuditLog()
    return exportAuditLogToCsv(entries, { projectName })
  },

  // Sync
  refreshComments: () => {
    const { provider } = get()
    if (!provider) return
    set({ comments: provider.getAll() })
  },
}))
